import { ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { resolveMediaUrl } from "@/lib/utils";
import type { PortfolioTemplate } from "./services/portfolio-api.service";

type TemplateProjectLike = {
	id?: string;
	title?: string;
	shortDescription?: string;
	thumbnailUrl?: string;
	imageUrls?: string[];
	category?: string;
};

export interface TemplateRenderProps {
	projects: TemplateProjectLike[];
	onOpenProject?: (projectId: string) => void;
	loadingProjectId?: string | null;
	mode?: "preview" | "public";
}

type TemplateRenderer = (props: TemplateRenderProps) => React.ReactElement;

export interface TemplateHeaderDesigner {
	id?: string;
	name: string;
	firmName?: string | null;
	profilePictureUrl?: string | null;
	bio?: string | null;
	isVerified?: boolean;
}

export interface TemplateHeaderProps {
	designer: TemplateHeaderDesigner;
	projectCount: number;
	signedIn: boolean;
	templateName: string;
	templateSummary: string;
}

export type TemplateHeaderRenderer = (
	props: TemplateHeaderProps,
) => React.ReactElement;

export interface PortfolioTemplateDefinition {
	id: PortfolioTemplate;
	name: string;
	description: string;
	changesSummary: string;
	pageWrapperClass: string;
	MiniPreview: TemplateRenderer;
	LivePreview: TemplateRenderer;
	Section: TemplateRenderer;
	Header: TemplateHeaderRenderer;
}

const DEFAULT_PLACEHOLDER_CLASS =
	"aspect-[4/3] rounded-lg bg-gradient-to-br from-dashboard-border via-dashboard-divider to-dashboard-surface text-dashboard-muted";

const STUDIO_PLACEHOLDER_CLASS = "bg-dashboard-warm-100 text-dashboard-muted";

function LocalPlaceholder({
	label,
	className = DEFAULT_PLACEHOLDER_CLASS,
}: {
	label: string;
	className?: string;
}) {
	return (
		<div
			className={`grid place-items-center text-[11px] font-medium uppercase tracking-wide ${className}`}
		>
			{label}
		</div>
	);
}

function Media({
	src,
	alt,
	className,
	fallbackLabel,
	placeholderClassName,
}: {
	src?: string;
	alt: string;
	className?: string;
	fallbackLabel: string;
	placeholderClassName?: string;
}): React.ReactElement {
	const resolved = resolveMediaUrl(src);
	if (!resolved) {
		return (
			<LocalPlaceholder
				label={fallbackLabel}
				className={placeholderClassName}
			/>
		);
	}

	return (
		<img
			src={resolved}
			alt={alt}
			className={className || "aspect-[4/3] w-full rounded-lg object-cover"}
		/>
	);
}

function cardBase(project: TemplateProjectLike | undefined, index: number) {
	return {
		title: project?.title || `Project ${index + 1}`,
		shortDescription: project?.shortDescription || "",
		thumbnailUrl: project?.thumbnailUrl,
		id: project?.id || `preview-${index}`,
		category: project?.category || "",
	};
}

function TemplateAMiniPreview({
	projects,
}: TemplateRenderProps): React.ReactElement {
	const items = [0, 1, 2].map((idx) => cardBase(projects[idx], idx));
	return (
		<div data-testid="template-a-mini" className="grid gap-2 sm:grid-cols-2">
			{items.map((item, idx) => (
				<div
					key={item.id}
					className={`overflow-hidden rounded-lg border bg-card ${idx === 2 ? "sm:col-span-2" : ""}`}
				>
					<Media
						src={item.thumbnailUrl}
						alt={item.title}
						fallbackLabel={`A ${idx + 1}`}
						className="h-20 w-full object-cover"
					/>
					<div className="p-2">
						<p className="truncate text-xs font-semibold">{item.title}</p>
					</div>
				</div>
			))}
		</div>
	);
}

function TemplateBMiniPreview({
	projects,
}: TemplateRenderProps): React.ReactElement {
	const hero = cardBase(projects[0], 0);
	const side1 = cardBase(projects[1], 1);
	const side2 = cardBase(projects[2], 2);

	return (
		<div data-testid="template-b-mini" className="grid grid-cols-3 gap-2">
			<div className="col-span-2 overflow-hidden rounded-lg border bg-card">
				<Media
					src={hero.thumbnailUrl}
					alt={hero.title}
					fallbackLabel="B Hero"
					className="h-24 w-full object-cover"
				/>
			</div>
			<div className="space-y-2">
				<div className="overflow-hidden rounded-lg border bg-card">
					<Media
						src={side1.thumbnailUrl}
						alt={side1.title}
						fallbackLabel="B 2"
						className="h-11 w-full object-cover"
					/>
				</div>
				<div className="overflow-hidden rounded-lg border bg-card">
					<Media
						src={side2.thumbnailUrl}
						alt={side2.title}
						fallbackLabel="B 3"
						className="h-11 w-full object-cover"
					/>
				</div>
			</div>
		</div>
	);
}

function TemplateALivePreview({
	projects,
}: TemplateRenderProps): React.ReactElement {
	const items = [0, 1, 2].map((idx) => cardBase(projects[idx], idx));
	return (
		<div
			data-testid="template-a-live"
			className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
		>
			{items.map((item) => (
				<div
					key={item.id}
					className="overflow-hidden rounded-xl border bg-card"
				>
					<Media
						src={item.thumbnailUrl}
						alt={item.title}
						fallbackLabel="Classic Grid"
						className="h-28 w-full object-cover"
					/>
					<div className="space-y-1 p-3">
						<p className="truncate text-sm font-semibold">{item.title}</p>
						<p className="line-clamp-2 text-xs text-muted-foreground">
							{item.shortDescription}
						</p>
					</div>
				</div>
			))}
		</div>
	);
}

function TemplateBLivePreview({
	projects,
}: TemplateRenderProps): React.ReactElement {
	const hero = cardBase(projects[0], 0);
	const side = [cardBase(projects[1], 1), cardBase(projects[2], 2)];

	return (
		<div data-testid="template-b-live" className="space-y-3">
			<div className="grid gap-3 md:grid-cols-3">
				<div className="md:col-span-2 overflow-hidden rounded-xl border bg-card">
					<Media
						src={hero.thumbnailUrl}
						alt={hero.title}
						fallbackLabel="Featured Story Hero"
						className="h-44 w-full object-cover"
					/>
					<div className="p-3">
						<p className="text-sm font-semibold">{hero.title}</p>
						<p className="line-clamp-2 text-xs text-muted-foreground">
							{hero.shortDescription}
						</p>
					</div>
				</div>
				<div className="space-y-3">
					{side.map((item) => (
						<div
							key={item.id}
							className="overflow-hidden rounded-xl border bg-card"
						>
							<Media
								src={item.thumbnailUrl}
								alt={item.title}
								fallbackLabel="Featured Story"
								className="h-20 w-full object-cover"
							/>
							<div className="p-2">
								<p className="truncate text-xs font-medium">{item.title}</p>
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}

function TemplateASection({
	projects,
	onOpenProject,
	loadingProjectId,
}: TemplateRenderProps): React.ReactElement {
	if (projects.length === 0) {
		return <StudioEmptySection testId="template-a-section" />;
	}

	return (
		<div
			data-testid="template-a-section"
			className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
		>
			{projects.map((project, index) => {
				const item = cardBase(project, index);
				return (
					<button
						key={item.id}
						type="button"
						aria-label={`View project: ${item.title}`}
						className="group overflow-hidden rounded-2xl border border-dashboard-border bg-dashboard-surface text-left shadow-sm transition hover:-translate-y-1 hover:shadow-xl cursor-pointer"
						onClick={() => onOpenProject?.(project.id || "")}
						disabled={!project.id || loadingProjectId === project.id}
					>
						<div className="relative overflow-hidden">
							<Media
								src={item.thumbnailUrl}
								alt={item.title}
								fallbackLabel="Project"
								className="aspect-[16/11] w-full object-cover transition duration-500 group-hover:scale-105"
							/>
							<div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-dashboard-dark/60 via-transparent to-transparent" />
							<div className="absolute top-3 left-3 right-3 flex items-center justify-between">
								<span className="rounded-full bg-dashboard-dark/70 px-2.5 py-0.5 font-mono text-[11px] font-semibold text-dashboard-on-fill backdrop-blur-sm">
									{String(index + 1).padStart(2, "0")}
								</span>
								{item.category ? (
									<span className="rounded-full bg-dashboard-surface/90 px-2.5 py-0.5 text-[11px] font-medium text-dashboard-text shadow-sm backdrop-blur-sm">
										{item.category}
									</span>
								) : null}
							</div>
						</div>
						<div className="space-y-1.5 p-5">
							<p className="font-heading text-base font-semibold text-dashboard-text transition group-hover:text-dashboard-accent-dark">
								{item.title}
							</p>
							<p className="line-clamp-2 text-xs leading-relaxed text-dashboard-muted">
								{item.shortDescription}
							</p>
							<div className="pt-2 flex items-center text-xs font-semibold text-dashboard-accent-dark">
								View project →
							</div>
						</div>
					</button>
				);
			})}
		</div>
	);
}

function TemplateBSection({
	projects,
	onOpenProject,
	loadingProjectId,
}: TemplateRenderProps): React.ReactElement {
	if (projects.length === 0) {
		return <StudioEmptySection testId="template-b-section" />;
	}

	const hero = projects[0];
	const heroItem = cardBase(hero, 0);
	const compact = projects.slice(1, 3);
	const rest = projects.slice(3);

	return (
		<div data-testid="template-b-section" className="space-y-8">
			<div
				className={`grid gap-6 ${compact.length > 0 ? "lg:grid-cols-3" : "grid-cols-1"}`}
			>
				<button
					type="button"
					className={`group overflow-hidden rounded-3xl border border-dashboard-border bg-dashboard-surface text-left shadow-sm transition hover:-translate-y-1 hover:shadow-xl cursor-pointer ${compact.length > 0 ? "lg:col-span-2" : "col-span-1"}`}
					onClick={() => onOpenProject?.(hero?.id || "")}
					disabled={!hero?.id || loadingProjectId === hero?.id}
				>
					<div className="relative overflow-hidden">
						<Media
							src={heroItem.thumbnailUrl}
							alt={heroItem.title}
							fallbackLabel="Hero"
							className="aspect-[16/9] w-full object-cover transition duration-700 group-hover:scale-105"
						/>
						<div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-dashboard-dark/70 via-transparent to-transparent" />
						{heroItem.category ? (
							<span className="absolute bottom-4 left-4 rounded-full bg-dashboard-accent px-3 py-1 text-xs font-semibold text-dashboard-on-fill shadow-md backdrop-blur-sm">
								{heroItem.category}
							</span>
						) : null}
					</div>
					<div className="space-y-2 p-6">
						<p className="font-heading text-xl font-bold text-dashboard-text">
							{heroItem.title}
						</p>
						<p className="line-clamp-3 text-sm leading-relaxed text-dashboard-muted">
							{heroItem.shortDescription}
						</p>
					</div>
				</button>

				{compact.length > 0 && (
					<div className="space-y-4">
						{compact.map((project, idx) => {
							const item = cardBase(project, idx + 1);
							return (
								<button
									key={item.id}
									type="button"
									className="group w-full overflow-hidden rounded-2xl border border-dashboard-border bg-dashboard-surface text-left shadow-sm transition hover:shadow-md cursor-pointer"
									onClick={() => onOpenProject?.(project.id || "")}
									disabled={!project.id || loadingProjectId === project.id}
								>
									<Media
										src={item.thumbnailUrl}
										alt={item.title}
										fallbackLabel={`Card ${idx + 2}`}
										className="h-32 w-full object-cover transition duration-500 group-hover:scale-105"
									/>
									<div className="p-4">
										<p className="font-heading truncate text-sm font-semibold text-dashboard-text">
											{item.title}
										</p>
										<p className="mt-1 line-clamp-1 text-xs text-dashboard-muted">
											{item.shortDescription}
										</p>
									</div>
								</button>
							);
						})}
					</div>
				)}
			</div>

			{rest.length > 0 ? (
				<div className="space-y-6">
					{rest.map((project, idx) => {
						const item = cardBase(project, idx + 3);
						const wide = idx % 2 === 0;
						return (
							<button
								key={item.id}
								type="button"
								className={`group grid w-full gap-0 overflow-hidden rounded-3xl border border-dashboard-border bg-dashboard-surface text-left shadow-sm transition hover:shadow-lg cursor-pointer ${wide ? "md:grid-cols-[1.4fr_1fr]" : "md:grid-cols-[1fr_1.4fr]"}`}
								onClick={() => onOpenProject?.(project.id || "")}
								disabled={!project.id || loadingProjectId === project.id}
							>
								{wide ? (
									<>
										<Media
											src={item.thumbnailUrl}
											alt={item.title}
											fallbackLabel="Wide"
											className="h-64 w-full object-cover transition duration-500 group-hover:scale-105"
										/>
										<div className="flex flex-col justify-center p-6 sm:p-8">
											{item.category ? (
												<span className="font-mono text-xs font-semibold text-dashboard-accent-dark">
													{item.category.toUpperCase()}
												</span>
											) : null}
											<p className="mt-1 font-heading text-lg font-bold text-dashboard-text">
												{item.title}
											</p>
											<p className="mt-2 line-clamp-4 text-sm leading-relaxed text-dashboard-muted">
												{item.shortDescription}
											</p>
										</div>
									</>
								) : (
									<>
										<div className="flex flex-col justify-center p-6 sm:p-8">
											{item.category ? (
												<span className="font-mono text-xs font-semibold text-dashboard-accent-dark">
													{item.category.toUpperCase()}
												</span>
											) : null}
											<p className="mt-1 font-heading text-lg font-bold text-dashboard-text">
												{item.title}
											</p>
											<p className="mt-2 line-clamp-4 text-sm leading-relaxed text-dashboard-muted">
												{item.shortDescription}
											</p>
										</div>
										<Media
											src={item.thumbnailUrl}
											alt={item.title}
											fallbackLabel="Wide"
											className="h-64 w-full object-cover transition duration-500 group-hover:scale-105"
										/>
									</>
								)}
							</button>
						);
					})}
				</div>
			) : null}
		</div>
	);
}

function StudioEmptySection({
	testId,
}: {
	testId: string;
}): React.ReactElement {
	return (
		<div
			data-testid={testId}
			className="rounded-xl border border-dashboard-border bg-dashboard-surface p-6 text-sm text-dashboard-muted"
		>
			No projects yet.
		</div>
	);
}

function MagazineSpreadMiniPreview({
	projects,
}: TemplateRenderProps): React.ReactElement {
	const hero = cardBase(projects[0], 0);
	const rows = [cardBase(projects[1], 1), cardBase(projects[2], 2)];

	return (
		<div data-testid="template-c-mini" className="space-y-1.5">
			<Media
				src={hero.thumbnailUrl}
				alt={hero.title}
				fallbackLabel="C Hero"
				className="h-14 w-full rounded-md object-cover"
				placeholderClassName={`h-14 rounded-md ${STUDIO_PLACEHOLDER_CLASS}`}
			/>
			{rows.map((item, idx) => (
				<div
					key={item.id}
					className={`flex items-center gap-1.5 ${idx % 2 === 1 ? "flex-row-reverse" : ""}`}
				>
					<div className="w-1/2 shrink-0">
						<Media
							src={item.thumbnailUrl}
							alt={item.title}
							fallbackLabel={`C ${idx + 2}`}
							className="h-9 w-full rounded-md object-cover"
							placeholderClassName={`h-9 rounded-md ${STUDIO_PLACEHOLDER_CLASS}`}
						/>
					</div>
					<p className="min-w-0 flex-1 truncate text-[11px] font-semibold text-dashboard-text">
						{item.title}
					</p>
				</div>
			))}
		</div>
	);
}

function MagazineSpreadLivePreview({
	projects,
}: TemplateRenderProps): React.ReactElement {
	const hero = cardBase(projects[0], 0);
	const rows = [cardBase(projects[1], 1), cardBase(projects[2], 2)];

	return (
		<div data-testid="template-c-live" className="space-y-3 bg-dashboard-bg">
			<div className="relative overflow-hidden">
				<Media
					src={hero.thumbnailUrl}
					alt={hero.title}
					fallbackLabel="Magazine Spread Hero"
					className="h-40 w-full object-cover"
					placeholderClassName={`h-40 ${STUDIO_PLACEHOLDER_CLASS}`}
				/>
				<div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-3">
					<p className="font-heading text-base font-semibold text-dashboard-on-fill">
						{hero.title}
					</p>
				</div>
			</div>
			{rows.map((item, idx) => (
				<div
					key={item.id}
					className="grid items-center gap-3 px-3 pb-3 sm:grid-cols-2"
				>
					<div className={idx % 2 === 1 ? "sm:order-2" : ""}>
						<Media
							src={item.thumbnailUrl}
							alt={item.title}
							fallbackLabel="Magazine Spread"
							className="h-24 w-full rounded-lg object-cover"
							placeholderClassName={`h-24 rounded-lg ${STUDIO_PLACEHOLDER_CLASS}`}
						/>
					</div>
					<div>
						<p className="font-heading text-sm font-semibold text-dashboard-text">
							{item.title}
						</p>
						<p className="line-clamp-2 text-xs text-dashboard-muted">
							{item.shortDescription}
						</p>
					</div>
				</div>
			))}
		</div>
	);
}

function MagazineSpreadSection({
	projects,
	onOpenProject,
	loadingProjectId,
}: TemplateRenderProps): React.ReactElement {
	if (projects.length === 0) {
		return <StudioEmptySection testId="template-c-section" />;
	}

	const [hero, ...rows] = projects;
	const heroItem = cardBase(hero, 0);

	return (
		<div data-testid="template-c-section" className="space-y-10">
			<button
				type="button"
				className="group relative block w-full overflow-hidden rounded-3xl border border-dashboard-border bg-dashboard-surface text-left shadow-lg transition hover:shadow-2xl cursor-pointer"
				onClick={() => onOpenProject?.(hero.id || "")}
				disabled={!hero.id || loadingProjectId === hero.id}
			>
				<Media
					src={heroItem.thumbnailUrl}
					alt={heroItem.title}
					fallbackLabel="Hero"
					className="aspect-[4/3] w-full object-cover transition duration-700 group-hover:scale-[1.03] sm:aspect-[21/9]"
					placeholderClassName={`aspect-[4/3] sm:aspect-[21/9] ${STUDIO_PLACEHOLDER_CLASS}`}
				/>
				<div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-dashboard-dark/85 via-dashboard-dark/40 to-transparent p-6 sm:p-10">
					<span className="font-mono text-xs font-bold tracking-widest text-dashboard-accent-light">
						{heroItem.category
							? `01 / ${heroItem.category.toUpperCase()}`
							: "01 / FEATURED"}
					</span>
					<p className="mt-1 font-heading text-2xl font-bold text-dashboard-on-fill sm:text-3xl">
						{heroItem.title}
					</p>
					<p className="mt-2 line-clamp-2 max-w-2xl text-sm leading-relaxed text-dashboard-on-fill/90">
						{heroItem.shortDescription}
					</p>
				</div>
			</button>

			{rows.map((project, idx) => {
				const item = cardBase(project, idx + 1);
				const imageRight = idx % 2 === 1;
				return (
					<button
						key={item.id}
						type="button"
						className="group grid w-full items-center gap-6 overflow-hidden rounded-3xl border border-dashboard-border bg-dashboard-surface p-5 text-left shadow-sm transition hover:shadow-lg md:grid-cols-2 md:gap-10 md:p-8 cursor-pointer"
						onClick={() => onOpenProject?.(project.id || "")}
						disabled={!project.id || loadingProjectId === project.id}
					>
						<div
							className={`overflow-hidden rounded-2xl ${imageRight ? "md:order-2" : ""}`}
						>
							<Media
								src={item.thumbnailUrl}
								alt={item.title}
								fallbackLabel="Project"
								className="aspect-[4/3] w-full object-cover transition duration-500 group-hover:scale-105"
								placeholderClassName={`aspect-[4/3] ${STUDIO_PLACEHOLDER_CLASS}`}
							/>
						</div>
						<div className="space-y-3">
							<span className="rounded-full bg-dashboard-warm-100 px-3 py-1 font-mono text-xs font-bold text-dashboard-accent-dark">
								{item.category
									? item.category.toUpperCase()
									: `PROJECT 0${idx + 2}`}
							</span>
							<p className="font-heading text-xl font-bold text-dashboard-text">
								{item.title}
							</p>
							<p className="line-clamp-4 text-sm leading-relaxed text-dashboard-muted">
								{item.shortDescription}
							</p>
						</div>
					</button>
				);
			})}
		</div>
	);
}

const GALLERY_PLACEHOLDER_RATIOS = [
	"aspect-[3/4]",
	"aspect-square",
	"aspect-[4/3]",
];

function galleryPlaceholderClass(index: number): string {
	const ratio =
		GALLERY_PLACEHOLDER_RATIOS[index % GALLERY_PLACEHOLDER_RATIOS.length];
	return `${ratio} ${STUDIO_PLACEHOLDER_CLASS}`;
}

function GalleryWallMiniPreview({
	projects,
}: TemplateRenderProps): React.ReactElement {
	const items = [0, 1, 2, 3, 4, 5].map((idx) => cardBase(projects[idx], idx));
	return (
		<div data-testid="template-d-mini" className="columns-3 gap-1.5">
			{items.map((item, idx) => (
				<div key={item.id} className="mb-1.5 break-inside-avoid">
					<Media
						src={item.thumbnailUrl}
						alt={item.title}
						fallbackLabel={`D ${idx + 1}`}
						className="w-full rounded-md"
						placeholderClassName={`rounded-md ${galleryPlaceholderClass(idx)}`}
					/>
				</div>
			))}
		</div>
	);
}

function GalleryWallLivePreview({
	projects,
}: TemplateRenderProps): React.ReactElement {
	const items = [0, 1, 2, 3, 4].map((idx) => cardBase(projects[idx], idx));
	return (
		<div
			data-testid="template-d-live"
			className="gap-3 bg-dashboard-bg p-3 sm:columns-2 lg:columns-3"
		>
			{items.map((item, idx) => (
				<figure key={item.id} className="mb-3 break-inside-avoid">
					<Media
						src={item.thumbnailUrl}
						alt={item.title}
						fallbackLabel="Gallery Wall"
						className="w-full rounded-lg"
						placeholderClassName={`rounded-lg ${galleryPlaceholderClass(idx)}`}
					/>
					<figcaption className="mt-1 truncate text-[11px] text-dashboard-muted">
						{item.title}
					</figcaption>
				</figure>
			))}
		</div>
	);
}

function GalleryWallSection({
	projects,
	onOpenProject,
	loadingProjectId,
}: TemplateRenderProps): React.ReactElement {
	if (projects.length === 0) {
		return <StudioEmptySection testId="template-d-section" />;
	}

	return (
		<div data-testid="template-d-section" className="space-y-6">
			<div className="gap-5 sm:columns-2 lg:columns-3">
				{projects.map((project, index) => {
					const item = cardBase(project, index);
					return (
						<button
							key={item.id}
							type="button"
							className="group mb-5 block w-full break-inside-avoid overflow-hidden rounded-2xl border border-dashboard-border bg-dashboard-surface text-left shadow-sm transition hover:-translate-y-1 hover:shadow-xl cursor-pointer"
							onClick={() => onOpenProject?.(project.id || "")}
							disabled={!project.id || loadingProjectId === project.id}
						>
							<div className="relative overflow-hidden">
								<Media
									src={item.thumbnailUrl}
									alt={item.title}
									fallbackLabel="Project"
									className="w-full transition duration-700 group-hover:scale-105"
									placeholderClassName={galleryPlaceholderClass(index)}
								/>
								<div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-dashboard-dark/65 via-transparent to-transparent opacity-0 transition duration-300 group-hover:opacity-100" />
							</div>
							<div className="p-4">
								<p className="font-heading text-sm font-semibold text-dashboard-text">
									{item.title}
								</p>
								<p className="mt-1 line-clamp-2 text-xs text-dashboard-muted">
									{item.shortDescription}
								</p>
							</div>
						</button>
					);
				})}
			</div>
		</div>
	);
}

function ArchitecturalSpecSection({
	projects,
	onOpenProject,
	loadingProjectId,
}: TemplateRenderProps): React.ReactElement {
	if (projects.length === 0) {
		return <StudioEmptySection testId="template-g-section" />;
	}

	return (
		<div data-testid="template-g-section" className="space-y-6">
			{projects.map((project, idx) => {
				const item = cardBase(project, idx);
				return (
					<button
						key={item.id}
						type="button"
						className="group grid w-full overflow-hidden rounded-2xl border border-dashboard-border bg-dashboard-surface text-left shadow-sm transition hover:shadow-lg md:grid-cols-[1.3fr_1fr] cursor-pointer"
						onClick={() => onOpenProject?.(project.id || "")}
						disabled={!project.id || loadingProjectId === project.id}
					>
						<div className="relative overflow-hidden">
							<Media
								src={item.thumbnailUrl}
								alt={item.title}
								fallbackLabel="Architectural View"
								className="aspect-[16/10] w-full object-cover transition duration-500 group-hover:scale-[1.02] md:h-full"
								placeholderClassName={`aspect-[16/10] md:h-full ${STUDIO_PLACEHOLDER_CLASS}`}
							/>
						</div>
						<div className="flex flex-col justify-between p-6">
							<div className="space-y-3">
								<div className="flex items-center justify-between">
									<span className="font-mono text-xs font-semibold tracking-widest text-dashboard-accent-dark">
										{String(idx + 1).padStart(2, "0")}
									</span>
									{item.category ? (
										<span className="rounded-md border border-dashboard-border bg-dashboard-bg px-2.5 py-1 text-xs font-medium text-dashboard-muted">
											{item.category}
										</span>
									) : null}
								</div>
								<h3 className="font-heading text-xl font-bold text-dashboard-text">
									{item.title}
								</h3>
								<p className="line-clamp-3 text-sm leading-relaxed text-dashboard-muted">
									{item.shortDescription}
								</p>
							</div>
							<div className="mt-6 flex items-center justify-between border-t border-dashboard-border pt-4">
								<span className="text-xs text-dashboard-muted">
									Click to view project details
								</span>
								<span className="inline-flex items-center rounded-md border border-dashboard-border bg-dashboard-surface px-3 py-1.5 text-xs font-medium text-dashboard-text">
									View Project
								</span>
							</div>
						</div>
					</button>
				);
			})}
		</div>
	);
}

function ArchitecturalSpecLivePreview({
	projects,
}: TemplateRenderProps): React.ReactElement {
	const items = [0, 1, 2].map((idx) => cardBase(projects[idx], idx));
	return (
		<div
			data-testid="template-g-live"
			className="space-y-3 bg-dashboard-bg p-3"
		>
			<div className="grid gap-3 sm:grid-cols-2">
				{items.map((item, idx) => (
					<div
						key={item.id}
						className="overflow-hidden rounded-xl border border-dashboard-border bg-card p-3"
					>
						<p className="font-mono text-xs font-semibold tracking-wider text-dashboard-accent-dark">
							0{idx + 1} / SPECIFICATION
						</p>
						<div className="mt-2 overflow-hidden rounded-lg">
							<Media
								src={item.thumbnailUrl}
								alt={item.title}
								fallbackLabel="Architectural Spec"
								className="h-24 w-full object-cover"
								placeholderClassName={`h-24 ${STUDIO_PLACEHOLDER_CLASS}`}
							/>
						</div>
						<p className="mt-2 font-heading text-sm font-semibold text-dashboard-text">
							{item.title}
						</p>
						<p className="line-clamp-2 text-xs text-dashboard-muted">
							{item.shortDescription}
						</p>
					</div>
				))}
			</div>
		</div>
	);
}

function ArchitecturalSpecMiniPreview({
	projects,
}: TemplateRenderProps): React.ReactElement {
	const items = [0, 1].map((idx) => cardBase(projects[idx], idx));
	return (
		<div data-testid="template-g-mini" className="space-y-1.5">
			{items.map((item, idx) => (
				<div
					key={item.id}
					className="flex items-center gap-2 rounded-md border border-dashboard-border bg-card p-1.5"
				>
					<div className="w-12 shrink-0">
						<Media
							src={item.thumbnailUrl}
							alt={item.title}
							fallbackLabel={`G ${idx + 1}`}
							className="h-8 w-full rounded object-cover"
							placeholderClassName={`h-8 rounded ${STUDIO_PLACEHOLDER_CLASS}`}
						/>
					</div>
					<div className="min-w-0 flex-1">
						<p className="font-mono text-[9px] font-semibold text-dashboard-accent-dark">
							0{idx + 1} / SPEC
						</p>
						<p className="truncate text-[10px] font-semibold text-dashboard-text">
							{item.title}
						</p>
					</div>
				</div>
			))}
		</div>
	);
}

function VibrantVignetteSection({
	projects,
	onOpenProject,
	loadingProjectId,
}: TemplateRenderProps): React.ReactElement {
	if (projects.length === 0) {
		return <StudioEmptySection testId="template-f-section" />;
	}

	return (
		<div data-testid="template-f-section" className="space-y-6">
			<div className="grid gap-6 sm:grid-cols-2">
				{projects.map((project, idx) => {
					const item = cardBase(project, idx);
					const secondaryImages =
						project.imageUrls?.filter(
							(url) => url && url !== project.thumbnailUrl,
						) || [];

					return (
						<button
							key={item.id}
							type="button"
							className="group block overflow-hidden rounded-2xl border border-dashboard-accent/30 bg-dashboard-surface text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg cursor-pointer"
							onClick={() => onOpenProject?.(project.id || "")}
							disabled={!project.id || loadingProjectId === project.id}
						>
							<div className="relative overflow-hidden">
								<Media
									src={item.thumbnailUrl}
									alt={item.title}
									fallbackLabel="Project View"
									className="aspect-[16/10] w-full object-cover transition duration-500 group-hover:scale-105"
									placeholderClassName={`aspect-[16/10] ${STUDIO_PLACEHOLDER_CLASS}`}
								/>
								<div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-dashboard-dark/50 via-transparent to-transparent" />
								<span className="absolute bottom-3 left-3 rounded-full bg-dashboard-accent/90 px-3 py-1 text-xs font-semibold text-dashboard-on-fill backdrop-blur-sm">
									{item.category || String(idx + 1).padStart(2, "0")}
								</span>
							</div>

							{secondaryImages.length > 0 ? (
								<div className="flex gap-2 border-y border-dashboard-border bg-dashboard-warm-100/40 p-3">
									{secondaryImages.slice(0, 3).map((imgUrl) => (
										<div
											key={`${item.id}-sec-${imgUrl}`}
											className="h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-dashboard-border"
										>
											<Media
												src={imgUrl}
												alt={`${item.title} detail`}
												fallbackLabel="Detail"
												className="h-full w-full object-cover"
												placeholderClassName={`h-full w-full ${STUDIO_PLACEHOLDER_CLASS}`}
											/>
										</div>
									))}
								</div>
							) : null}

							<div className="p-5">
								<p className="font-display text-lg font-bold text-dashboard-text">
									{item.title}
								</p>
								<p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-dashboard-muted">
									{item.shortDescription}
								</p>
							</div>
						</button>
					);
				})}
			</div>
		</div>
	);
}

function VibrantVignetteLivePreview({
	projects,
}: TemplateRenderProps): React.ReactElement {
	const items = [0, 1, 2].map((idx) => cardBase(projects[idx], idx));
	return (
		<div
			data-testid="template-f-live"
			className="space-y-3 bg-dashboard-bg p-3"
		>
			<div className="grid gap-3 sm:grid-cols-2">
				{items.map((item, idx) => (
					<div
						key={item.id}
						className="overflow-hidden rounded-xl border border-dashboard-accent/25 bg-card"
					>
						<div className="relative">
							<Media
								src={item.thumbnailUrl}
								alt={item.title}
								fallbackLabel="Vibrant Vignette"
								className="h-28 w-full object-cover"
								placeholderClassName={`h-28 ${STUDIO_PLACEHOLDER_CLASS}`}
							/>
							<span className="absolute bottom-2 left-2 rounded-full bg-dashboard-dark/60 px-2 py-0.5 text-[10px] font-medium text-dashboard-on-fill backdrop-blur-sm">
								{item.category || `Project ${idx + 1}`}
							</span>
						</div>
						<div className="p-3">
							<p className="font-display text-sm font-bold text-dashboard-text">
								{item.title}
							</p>
							<p className="mt-0.5 line-clamp-2 text-xs text-dashboard-muted">
								{item.shortDescription}
							</p>
						</div>
					</div>
				))}
			</div>
		</div>
	);
}

function VibrantVignetteMiniPreview({
	projects,
}: TemplateRenderProps): React.ReactElement {
	const items = [0, 1, 2].map((idx) => cardBase(projects[idx], idx));
	return (
		<div data-testid="template-f-mini" className="space-y-1.5">
			{items.slice(0, 2).map((item, idx) => (
				<div
					key={item.id}
					className="overflow-hidden rounded-md border border-dashboard-accent/30 bg-card p-1"
				>
					<div className="grid grid-cols-3 gap-1">
						<div className="col-span-2">
							<Media
								src={item.thumbnailUrl}
								alt={item.title}
								fallbackLabel={`Vignette ${idx + 1}`}
								className="h-10 w-full rounded object-cover"
								placeholderClassName={`h-10 rounded ${STUDIO_PLACEHOLDER_CLASS}`}
							/>
						</div>
						<div className="grid place-items-center rounded bg-dashboard-warm-100 text-[9px] font-bold text-dashboard-accent-dark">
							{idx + 1}
						</div>
					</div>
					<p className="mt-1 truncate text-[10px] font-semibold text-dashboard-text">
						{item.title}
					</p>
				</div>
			))}
		</div>
	);
}

function BoutiqueStudioSection({
	projects,
	onOpenProject,
	loadingProjectId,
}: TemplateRenderProps): React.ReactElement {
	if (projects.length === 0) {
		return <StudioEmptySection testId="template-e-section" />;
	}

	return (
		<div data-testid="template-e-section" className="space-y-8">
			<div className="grid gap-6 sm:grid-cols-2">
				{projects.map((project, idx) => {
					const item = cardBase(project, idx);
					const isFeatured = idx === 0;
					return (
						<button
							key={item.id}
							type="button"
							className={`group block overflow-hidden rounded-2xl border border-dashboard-border bg-dashboard-surface text-left shadow-sm transition hover:shadow-md cursor-pointer ${isFeatured ? "sm:col-span-2" : ""}`}
							onClick={() => onOpenProject?.(project.id || "")}
							disabled={!project.id || loadingProjectId === project.id}
						>
							<div className="relative overflow-hidden">
								<Media
									src={item.thumbnailUrl}
									alt={item.title}
									fallbackLabel="Studio Project"
									className={
										isFeatured
											? "aspect-[16/9] w-full object-cover transition duration-500 group-hover:scale-[1.02]"
											: "aspect-[4/3] w-full object-cover transition duration-500 group-hover:scale-[1.02]"
									}
									placeholderClassName={
										isFeatured
											? `aspect-[16/9] ${STUDIO_PLACEHOLDER_CLASS}`
											: `aspect-[4/3] ${STUDIO_PLACEHOLDER_CLASS}`
									}
								/>
							</div>
							<div className="p-5">
								<div className="flex items-center justify-between gap-2">
									<p className="font-heading text-lg font-semibold text-dashboard-text">
										{item.title}
									</p>
									<span className="rounded-md border border-dashboard-border bg-dashboard-bg px-2 py-0.5 text-[11px] font-medium text-dashboard-muted">
										View
									</span>
								</div>
								<p className="mt-2 line-clamp-2 text-sm text-dashboard-muted">
									{item.shortDescription}
								</p>
							</div>
						</button>
					);
				})}
			</div>
		</div>
	);
}

function BoutiqueStudioLivePreview({
	projects,
}: TemplateRenderProps): React.ReactElement {
	const items = [0, 1, 2].map((idx) => cardBase(projects[idx], idx));
	return (
		<div
			data-testid="template-e-live"
			className="space-y-3 bg-dashboard-bg p-3"
		>
			<div className="grid gap-3 sm:grid-cols-2">
				{items.map((item, idx) => (
					<div
						key={item.id}
						className={`overflow-hidden rounded-xl border border-dashboard-border bg-card ${idx === 0 ? "sm:col-span-2" : ""}`}
					>
						<Media
							src={item.thumbnailUrl}
							alt={item.title}
							fallbackLabel="Boutique Studio"
							className={
								idx === 0
									? "h-36 w-full object-cover"
									: "h-24 w-full object-cover"
							}
							placeholderClassName={
								idx === 0
									? `h-36 ${STUDIO_PLACEHOLDER_CLASS}`
									: `h-24 ${STUDIO_PLACEHOLDER_CLASS}`
							}
						/>
						<div className="p-3">
							<p className="font-heading text-sm font-semibold text-dashboard-text">
								{item.title}
							</p>
							<p className="mt-1 line-clamp-2 text-xs text-dashboard-muted">
								{item.shortDescription}
							</p>
						</div>
					</div>
				))}
			</div>
		</div>
	);
}

function BoutiqueStudioMiniPreview({
	projects,
}: TemplateRenderProps): React.ReactElement {
	const items = [0, 1, 2].map((idx) => cardBase(projects[idx], idx));
	return (
		<div data-testid="template-e-mini" className="space-y-1.5">
			<div className="overflow-hidden rounded-md border border-dashboard-border bg-card">
				<Media
					src={items[0].thumbnailUrl}
					alt={items[0].title}
					fallbackLabel="Studio 1"
					className="h-14 w-full object-cover"
					placeholderClassName={`h-14 ${STUDIO_PLACEHOLDER_CLASS}`}
				/>
				<div className="p-1.5">
					<p className="truncate text-[10px] font-semibold">{items[0].title}</p>
				</div>
			</div>
			<div className="grid grid-cols-2 gap-1.5">
				{items.slice(1).map((item, idx) => (
					<div
						key={item.id}
						className="overflow-hidden rounded-md border border-dashboard-border bg-card"
					>
						<Media
							src={item.thumbnailUrl}
							alt={item.title}
							fallbackLabel={`Studio ${idx + 2}`}
							className="h-10 w-full object-cover"
							placeholderClassName={`h-10 ${STUDIO_PLACEHOLDER_CLASS}`}
						/>
						<div className="p-1">
							<p className="truncate text-[9px] font-medium">{item.title}</p>
						</div>
					</div>
				))}
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// 7 Holistically Branded Portfolio Headers (Top-to-Bottom Visual Transformations)
// Each header transforms logo/wordmark, typography, bio, accents, and atmosphere.
// ---------------------------------------------------------------------------

function TemplateAHeader({
	designer,
	projectCount,
	signedIn: _signedIn,
}: TemplateHeaderProps): React.ReactElement {
	const initial = (designer.name || "D").trim().charAt(0).toUpperCase() || "D";
	const profileUrl = resolveMediaUrl(designer.profilePictureUrl);

	return (
		<header
			data-testid="template-a-header"
			className="relative overflow-hidden rounded-2xl border border-neutral-800 bg-[#0d0e12] p-6 sm:p-10 text-neutral-100 shadow-2xl"
		>
			<div
				className="pointer-events-none absolute inset-0 opacity-[0.03]"
				style={{
					backgroundImage:
						"linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
					backgroundSize: "32px 32px",
				}}
			/>

			{/* Top Bar */}
			<div className="relative z-10 flex flex-wrap items-center justify-between gap-3 border-b border-neutral-800/80 pb-4 text-[11px] font-mono tracking-widest text-neutral-400 uppercase">
				<div className="flex items-center gap-2">
					<span className="inline-block h-2 w-2 rounded-none bg-neutral-100" />
					<span>PORTFOLIO</span>
				</div>
				<div className="flex items-center gap-4">
					<span>
						{projectCount} {projectCount === 1 ? "PROJECT" : "PROJECTS"}
					</span>
					{designer.isVerified && (
						<span className="flex items-center gap-1 text-emerald-400">
							<ShieldCheck className="h-3.5 w-3.5" />
							<span>VERIFIED</span>
						</span>
					)}
				</div>
			</div>

			{/* Main Branding Area */}
			<div className="relative z-10 mt-6 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
				<div className="flex items-center gap-6">
					<div className="relative shrink-0">
						{profileUrl ? (
							<img
								src={profileUrl}
								alt={`${designer.name} profile`}
								className="h-20 w-20 sm:h-24 sm:w-24 rounded border border-neutral-700 bg-neutral-900 object-cover shadow-lg"
							/>
						) : (
							<div
								data-testid="public-profile-initial"
								className="grid h-20 w-20 sm:h-24 sm:w-24 place-items-center rounded border border-neutral-700 bg-neutral-900 text-2xl sm:text-3xl font-black font-mono text-neutral-100"
							>
								{initial}
							</div>
						)}
					</div>

					<div className="min-w-0">
						<h1 className="mt-1 font-mono text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-white">
							{designer.firmName || designer.name}
						</h1>
						{designer.firmName && (
							<p className="mt-1 text-xs sm:text-sm font-mono text-neutral-400 tracking-wide">
								PRINCIPAL: {designer.name}
							</p>
						)}
						{designer.bio && (
							<p className="mt-3 max-w-xl text-xs sm:text-sm leading-relaxed text-neutral-400 font-mono">
								{designer.bio}
							</p>
						)}
					</div>
				</div>

				{/* Right Stats & Status */}
				<div className="flex flex-wrap items-center gap-3 font-mono text-xs">
					<div className="rounded border border-neutral-800 bg-neutral-900/80 px-4 py-3">
						<span className="block text-[10px] text-neutral-500 uppercase">
							PROJECTS
						</span>
						<span className="text-xl font-bold text-white">
							{String(projectCount).padStart(2, "0")}
						</span>
					</div>
				</div>
			</div>
		</header>
	);
}

function TemplateBHeader({
	designer,
	projectCount,
	signedIn: _signedIn,
}: TemplateHeaderProps): React.ReactElement {
	const initial = (designer.name || "D").trim().charAt(0).toUpperCase() || "D";
	const profileUrl = resolveMediaUrl(designer.profilePictureUrl);

	return (
		<header
			data-testid="template-b-header"
			className="relative overflow-hidden rounded-2xl border border-[#E7E2D7] bg-[#FAF8F5] p-8 sm:p-14 text-[#2D2A26] shadow-sm text-center"
		>
			<div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full border border-[#D5CFC2] text-xs font-serif tracking-widest text-[#787165]">
				✦
			</div>

			<div className="mt-4 space-y-1">
				<p className="font-serif italic text-xs sm:text-sm tracking-[0.25em] text-[#827A6D] uppercase">
					PORTFOLIO
				</p>
				<h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light uppercase tracking-[0.28em] text-[#2D2A26]">
					{designer.firmName || designer.name}
				</h1>
				{designer.firmName && (
					<p className="text-[11px] font-sans tracking-[0.2em] text-[#938B7E] uppercase pt-1">
						PRINCIPAL DESIGNER: {designer.name.toUpperCase()}
					</p>
				)}
			</div>

			{designer.bio && (
				<div className="my-6 mx-auto max-w-xl">
					<p className="text-xs sm:text-sm text-[#736B5E] leading-relaxed max-w-md mx-auto">
						{designer.bio}
					</p>
				</div>
			)}

			<div className="mt-6 flex flex-col items-center justify-center gap-3">
				<div className="relative">
					{profileUrl ? (
						<img
							src={profileUrl}
							alt={`${designer.name} profile`}
							className="h-16 w-16 sm:h-20 sm:w-20 rounded-full border-2 border-[#D5CFC2] object-cover ring-4 ring-[#FAF8F5] shadow-sm"
						/>
					) : (
						<div
							data-testid="public-profile-initial"
							className="grid h-16 w-16 sm:h-20 sm:w-20 place-items-center rounded-full border-2 border-[#D5CFC2] bg-[#EFECE4] text-xl sm:text-2xl font-serif text-[#443F38]"
						>
							{initial}
						</div>
					)}
					{designer.isVerified && (
						<div className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-[#2D2A26] text-white">
							<ShieldCheck className="h-3.5 w-3.5" />
						</div>
					)}
				</div>

				<div className="flex flex-wrap items-center justify-center gap-4 text-xs font-serif tracking-widest text-[#7D766A] uppercase pt-2">
					<span>
						{projectCount} {projectCount === 1 ? "PROJECT" : "PROJECTS"}
					</span>
				</div>
			</div>
		</header>
	);
}

function TemplateCHeader({
	designer,
	projectCount,
	signedIn: _signedIn,
}: TemplateHeaderProps): React.ReactElement {
	const initial = (designer.name || "D").trim().charAt(0).toUpperCase() || "D";
	const profileUrl = resolveMediaUrl(designer.profilePictureUrl);

	return (
		<header
			data-testid="template-c-header"
			className="relative overflow-hidden rounded-3xl border-2 border-indigo-100 bg-white p-6 sm:p-10 text-slate-900 shadow-xl"
		>
			<div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-gradient-to-br from-rose-300 to-amber-200 blur-3xl opacity-40" />
			<div className="pointer-events-none absolute -bottom-16 -left-16 h-64 w-64 rounded-full bg-gradient-to-tr from-indigo-300 to-emerald-200 blur-3xl opacity-40" />

			<div className="relative z-10 flex flex-wrap items-center justify-between gap-2">
				<div className="flex items-center gap-2 flex-wrap">
					<span className="rounded-full bg-rose-500 px-3.5 py-1 text-xs font-black uppercase tracking-wider text-white shadow-md transform -rotate-1">
						PORTFOLIO
					</span>
				</div>
				{designer.isVerified && (
					<span className="flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-800 border border-emerald-300">
						<ShieldCheck className="h-3.5 w-3.5" />
						VERIFIED
					</span>
				)}
			</div>

			<div className="relative z-10 mt-6 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
				<div className="flex items-center gap-5 sm:gap-6">
					<div className="relative shrink-0">
						{profileUrl ? (
							<img
								src={profileUrl}
								alt={`${designer.name} profile`}
								className="h-20 w-20 sm:h-24 sm:w-24 rounded-full object-cover ring-4 ring-rose-400 ring-offset-4 ring-offset-amber-200 shadow-xl"
							/>
						) : (
							<div
								data-testid="public-profile-initial"
								className="grid h-20 w-20 sm:h-24 sm:w-24 place-items-center rounded-full bg-gradient-to-tr from-rose-500 via-amber-500 to-indigo-600 text-3xl font-black text-white ring-4 ring-rose-400 ring-offset-4 ring-offset-amber-200 shadow-xl"
							>
								{initial}
							</div>
						)}
					</div>

					<div className="min-w-0">
						<h1 className="mt-1 text-3xl sm:text-5xl font-black tracking-tight bg-gradient-to-r from-rose-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
							{designer.firmName || designer.name}
						</h1>
						{designer.firmName && (
							<p className="mt-1 text-xs sm:text-sm font-bold text-rose-600 tracking-wide uppercase">
								BY {designer.name.toUpperCase()}
							</p>
						)}
						{designer.bio && (
							<p className="mt-2 max-w-lg text-sm sm:text-base font-medium text-slate-700">
								{designer.bio}
							</p>
						)}
					</div>
				</div>

				<div className="flex flex-wrap items-center gap-3">
					<div className="rounded-2xl border-2 border-indigo-200 bg-indigo-50 px-5 py-3 shadow-sm transform rotate-1">
						<p className="text-[11px] font-bold uppercase tracking-wider text-indigo-500">
							Showcase
						</p>
						<p className="text-2xl font-black text-indigo-700">
							{projectCount} {projectCount === 1 ? "Project" : "Projects"}
						</p>
					</div>
				</div>
			</div>
		</header>
	);
}

function TemplateDHeader({
	designer,
	projectCount,
	signedIn: _signedIn,
}: TemplateHeaderProps): React.ReactElement {
	const initial = (designer.name || "D").trim().charAt(0).toUpperCase() || "D";
	const profileUrl = resolveMediaUrl(designer.profilePictureUrl);

	return (
		<header
			data-testid="template-d-header"
			className="relative overflow-hidden rounded-2xl border border-[#DBC89F] bg-[#FCFAF6] p-8 sm:p-14 text-[#1C1814] shadow-md text-center"
		>
			<div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border-2 border-[#C5A059] bg-[#1C1814] text-[#E8CE90] shadow-lg">
				<span className="font-serif text-lg font-bold tracking-widest">
					{initial}
				</span>
			</div>

			<div className="mt-4 space-y-1">
				<h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal uppercase tracking-[0.2em] text-[#1C1814]">
					{designer.firmName || designer.name}
				</h1>
				<p className="font-serif text-xs sm:text-sm tracking-[0.3em] uppercase text-[#C5A059] font-medium pt-1">
					PORTFOLIO
				</p>
				{designer.firmName && (
					<p className="text-[11px] font-sans tracking-[0.25em] text-[#7A7165] uppercase pt-0.5">
						FOUNDER & PRINCIPAL: {designer.name.toUpperCase()}
					</p>
				)}
			</div>

			<div className="my-5 h-[1px] w-48 sm:w-64 mx-auto bg-gradient-to-r from-transparent via-[#C5A059] to-transparent" />

			{designer.bio && (
				<p className="mt-2 text-xs sm:text-sm text-[#756C60] max-w-lg mx-auto leading-relaxed">
					{designer.bio}
				</p>
			)}

			<div className="mt-6 flex flex-col items-center justify-center gap-3">
				<div className="relative">
					{profileUrl ? (
						<img
							src={profileUrl}
							alt={`${designer.name} profile`}
							className="h-16 w-16 sm:h-20 sm:w-20 rounded-full border-2 border-[#C5A059] object-cover ring-4 ring-[#FCFAF6] shadow-md"
						/>
					) : (
						<div
							data-testid="public-profile-initial"
							className="grid h-16 w-16 sm:h-20 sm:w-20 place-items-center rounded-full border-2 border-[#C5A059] bg-[#1C1814] text-xl font-serif text-[#E8CE90]"
						>
							{initial}
						</div>
					)}
					{designer.isVerified && (
						<div className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-[#C5A059] text-white">
							<ShieldCheck className="h-3.5 w-3.5" />
						</div>
					)}
				</div>

				<div className="flex flex-wrap items-center justify-center gap-3 text-xs font-serif tracking-[0.2em] text-[#8C7646] uppercase pt-2">
					<span>
						{projectCount} {projectCount === 1 ? "PROJECT" : "PROJECTS"}
					</span>
				</div>
			</div>
		</header>
	);
}

function TemplateEHeader({
	designer,
	projectCount,
	signedIn: _signedIn,
}: TemplateHeaderProps): React.ReactElement {
	const initial = (designer.name || "D").trim().charAt(0).toUpperCase() || "D";
	const profileUrl = resolveMediaUrl(designer.profilePictureUrl);
	const displayName = designer.firmName || designer.name;

	return (
		<header
			data-testid="template-e-header"
			className="relative overflow-hidden rounded-2xl border border-[#E3E0D8] bg-[#F7F6F3] p-8 sm:p-12 text-[#2F2E2B]"
		>
			<div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
				<div className="space-y-2">
					<div className="text-[11px] font-sans tracking-[0.25em] text-[#8C887E] uppercase">
						portfolio
					</div>
					<h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-[#2F2E2B] lowercase tracking-normal">
						{displayName.toLowerCase()}
					</h1>
					{designer.firmName && (
						<p className="text-xs text-[#9E9A90] font-light">
							by {designer.name}
						</p>
					)}
					{designer.bio && (
						<p className="mt-4 max-w-xl text-xs sm:text-sm font-light leading-relaxed text-[#6E6B63]">
							{designer.bio}
						</p>
					)}
				</div>

				<div className="flex items-center gap-4 lg:flex-col lg:items-end">
					<div className="relative">
						{profileUrl ? (
							<img
								src={profileUrl}
								alt={`${designer.name} profile`}
								className="h-16 w-16 sm:h-20 sm:w-20 rounded-full border border-[#D5D2C9] object-cover shadow-sm"
							/>
						) : (
							<div
								data-testid="public-profile-initial"
								className="grid h-16 w-16 sm:h-20 sm:w-20 place-items-center rounded-full border border-[#D5D2C9] bg-[#EFECE5] text-xl font-serif text-[#4A4742]"
							>
								{initial}
							</div>
						)}
						{designer.isVerified && (
							<div className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#525049] text-white">
								<ShieldCheck className="h-3 w-3" />
							</div>
						)}
					</div>
					<div className="text-right">
						<span className="font-serif text-xs tracking-widest text-[#8C887E] uppercase">
							SELECTED WORKS ({String(projectCount).padStart(2, "0")})
						</span>
					</div>
				</div>
			</div>
		</header>
	);
}

function TemplateFHeader({
	designer,
	projectCount,
	signedIn: _signedIn,
}: TemplateHeaderProps): React.ReactElement {
	const initial = (designer.name || "D").trim().charAt(0).toUpperCase() || "D";
	const profileUrl = resolveMediaUrl(designer.profilePictureUrl);

	return (
		<header
			data-testid="template-f-header"
			className="relative overflow-hidden rounded-3xl border border-[#F2D7CD] bg-[#FAF5EF] p-6 sm:p-10 text-[#292524] shadow-sm"
		>
			<div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#F0D5CA] pb-4">
				<span className="inline-block rounded-md bg-[#C25E3E] px-3 py-1 text-xs font-black uppercase tracking-wider text-white">
					PORTFOLIO
				</span>
				{designer.isVerified && (
					<span className="flex items-center gap-1 text-xs font-bold text-[#C25E3E]">
						<ShieldCheck className="h-4 w-4" />
						VERIFIED
					</span>
				)}
			</div>

			<div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
				<div className="flex items-center gap-5 sm:gap-6">
					<div className="relative shrink-0">
						{profileUrl ? (
							<img
								src={profileUrl}
								alt={`${designer.name} profile`}
								className="h-20 w-20 sm:h-24 sm:w-24 rounded-2xl border-2 border-[#C25E3E]/40 object-cover shadow-md"
							/>
						) : (
							<div
								data-testid="public-profile-initial"
								className="grid h-20 w-20 sm:h-24 sm:w-24 place-items-center rounded-2xl border-2 border-[#C25E3E]/40 bg-[#FBECE6] text-3xl font-black text-[#C25E3E]"
							>
								{initial}
							</div>
						)}
					</div>

					<div className="min-w-0">
						<h1 className="text-3xl sm:text-5xl font-black tracking-tight text-[#292524] uppercase">
							{designer.firmName || designer.name}
						</h1>
						{designer.firmName && (
							<p className="mt-0.5 text-xs font-bold text-stone-500 uppercase">
								PRINCIPAL: {designer.name}
							</p>
						)}
						{designer.bio && (
							<p className="mt-2 text-xs sm:text-sm text-[#78716C] max-w-xl">
								{designer.bio}
							</p>
						)}
					</div>
				</div>

				<div className="flex flex-wrap items-center gap-3">
					<div className="rounded-xl border border-[#F0D5CA] bg-white px-4 py-2.5 text-center shadow-xs">
						<span className="block text-[10px] font-bold uppercase text-[#C25E3E]">
							PROJECTS
						</span>
						<span className="text-xl font-black text-[#292524]">
							{projectCount}
						</span>
					</div>
				</div>
			</div>
		</header>
	);
}

function TemplateGHeader({
	designer,
	projectCount,
	signedIn: _signedIn,
}: TemplateHeaderProps): React.ReactElement {
	const initial = (designer.name || "D").trim().charAt(0).toUpperCase() || "D";
	const profileUrl = resolveMediaUrl(designer.profilePictureUrl);

	return (
		<header
			data-testid="template-g-header"
			className="relative overflow-hidden rounded-xl border border-zinc-200 bg-white p-6 sm:p-10 text-zinc-900 shadow-xs"
		>
			<div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-200 pb-4 font-mono text-[11px] tracking-wider text-zinc-500 uppercase">
				<div className="flex items-center gap-2">
					<span className="inline-block h-2 w-2 rounded-full bg-zinc-900" />
					<span>PORTFOLIO</span>
				</div>
				<div className="flex items-center gap-4">
					<span>
						{projectCount} {projectCount === 1 ? "PROJECT" : "PROJECTS"}
					</span>
					{designer.isVerified && (
						<span className="flex items-center gap-1 text-zinc-900 font-bold">
							<ShieldCheck className="h-3.5 w-3.5" />
							VERIFIED
						</span>
					)}
				</div>
			</div>

			<div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
				<div className="flex items-center gap-5 sm:gap-6">
					<div className="relative shrink-0">
						{profileUrl ? (
							<img
								src={profileUrl}
								alt={`${designer.name} profile`}
								className="h-20 w-20 sm:h-24 sm:w-24 rounded border border-zinc-300 object-cover"
							/>
						) : (
							<div
								data-testid="public-profile-initial"
								className="grid h-20 w-20 sm:h-24 sm:w-24 place-items-center rounded border border-zinc-300 bg-zinc-100 font-mono text-2xl font-bold text-zinc-800"
							>
								{initial}
							</div>
						)}
					</div>

					<div className="min-w-0">
						<h1 className="font-sans text-2xl sm:text-4xl font-semibold uppercase tracking-[0.2em] text-zinc-900">
							{designer.firmName || designer.name}
						</h1>
						{designer.firmName && (
							<p className="font-mono text-xs tracking-widest uppercase text-zinc-500 mt-1">
								PRINCIPAL: {designer.name}
							</p>
						)}
						{designer.bio && (
							<p className="mt-2 text-xs sm:text-sm text-zinc-600 max-w-xl leading-relaxed">
								{designer.bio}
							</p>
						)}
					</div>
				</div>

				<div className="grid grid-cols-2 gap-2 font-mono text-xs">
					<div className="rounded border border-zinc-200 bg-zinc-50 p-3">
						<span className="block text-[10px] text-zinc-400 uppercase">
							PROJECTS
						</span>
						<span className="font-semibold text-zinc-800">{projectCount}</span>
					</div>
					<div className="rounded border border-zinc-200 bg-zinc-50 p-3">
						<span className="block text-[10px] text-zinc-400 uppercase">
							STATUS
						</span>
						<span className="font-semibold text-zinc-800">ACTIVE</span>
					</div>
				</div>
			</div>
		</header>
	);
}

const TEMPLATE_REGISTRY: Record<
	PortfolioTemplate,
	PortfolioTemplateDefinition
> = {
	template_a: {
		id: "template_a",
		name: "Classic Grid",
		description: "Architectural dark grid layout with clean visual rhythm.",
		changesSummary:
			"Equal project emphasis, category chips, and clean visual rhythm.",
		pageWrapperClass: "min-h-screen bg-[#0d0e12] text-neutral-100",
		MiniPreview: TemplateAMiniPreview,
		LivePreview: TemplateALivePreview,
		Section: TemplateASection,
		Header: TemplateAHeader,
	},
	template_b: {
		id: "template_b",
		name: "Featured Story",
		description:
			"Warm luxury storytelling layout with narrative editorial rows.",
		changesSummary:
			"Editorial hero strip, brand quote, and alternating narrative rows.",
		pageWrapperClass: "min-h-screen bg-[#FAF8F5] text-[#2D2A26]",
		MiniPreview: TemplateBMiniPreview,
		LivePreview: TemplateBLivePreview,
		Section: TemplateBSection,
		Header: TemplateBHeader,
	},
	template_c: {
		id: "template_c",
		name: "Magazine Spread",
		description:
			"Vibrant visual chapters with high-contrast color accents and spreads.",
		changesSummary:
			"Full-bleed hero banner and numbered, vibrant chapter spreads.",
		pageWrapperClass: "min-h-screen bg-[#FAFAFA] text-slate-900",
		MiniPreview: MagazineSpreadMiniPreview,
		LivePreview: MagazineSpreadLivePreview,
		Section: MagazineSpreadSection,
		Header: TemplateCHeader,
	},
	template_d: {
		id: "template_d",
		name: "Gallery Wall",
		description:
			"Curated multi-column masonry wall highlighting rich textures and finishes.",
		changesSummary:
			"Curated photo waterfall letting rich textures and spaces shine.",
		pageWrapperClass: "min-h-screen bg-[#FCFAF6] text-[#1C1814]",
		MiniPreview: GalleryWallMiniPreview,
		LivePreview: GalleryWallLivePreview,
		Section: GalleryWallSection,
		Header: TemplateDHeader,
	},
	template_e: {
		id: "template_e",
		name: "Boutique Studio",
		description:
			"Serene architectural minimalism with generous whitespace and warm tones.",
		changesSummary:
			"Spacious whitespace, asymmetric cards, and warm residential simplicity.",
		pageWrapperClass: "min-h-screen bg-[#F7F6F3] text-[#2F2E2B]",
		MiniPreview: BoutiqueStudioMiniPreview,
		LivePreview: BoutiqueStudioLivePreview,
		Section: BoutiqueStudioSection,
		Header: TemplateEHeader,
	},
	template_f: {
		id: "template_f",
		name: "Vibrant Vignette",
		description:
			"Multi-photo transformation clusters showcasing finishes, fixtures, and reveals.",
		changesSummary:
			"Multi-photo collage cards showcasing finishes, fixtures, and reveals.",
		pageWrapperClass: "min-h-screen bg-[#FAF5EF] text-[#292524]",
		MiniPreview: VibrantVignetteMiniPreview,
		LivePreview: VibrantVignetteLivePreview,
		Section: VibrantVignetteSection,
		Header: TemplateFHeader,
	},
	template_g: {
		id: "template_g",
		name: "Architectural Spec",
		description:
			"Structured case study format with technical specification sheets.",
		changesSummary:
			"Split-screen cards with landscape photography and technical spec sheets.",
		pageWrapperClass: "min-h-screen bg-[#F8F9FA] text-zinc-900",
		MiniPreview: ArchitecturalSpecMiniPreview,
		LivePreview: ArchitecturalSpecLivePreview,
		Section: ArchitecturalSpecSection,
		Header: TemplateGHeader,
	},
};

export const PORTFOLIO_TEMPLATES: PortfolioTemplateDefinition[] =
	Object.values(TEMPLATE_REGISTRY);

/** Looks a template up by id, falling back to `template_a` for an unknown id. */
export function getPortfolioTemplate(
	template: PortfolioTemplate,
): PortfolioTemplateDefinition {
	if (template && template in TEMPLATE_REGISTRY) {
		return TEMPLATE_REGISTRY[template];
	}
	return TEMPLATE_REGISTRY.template_a;
}

export { normalizeContractorTemplate } from "./services/portfolio-api.service";

export function renderMiniTemplatePreview(
	template: PortfolioTemplate,
	projects: TemplateProjectLike[],
): React.ReactElement {
	const { MiniPreview } = getPortfolioTemplate(template);
	return <MiniPreview projects={projects} mode="preview" />;
}

export function renderLiveTemplatePreview(
	template: PortfolioTemplate,
	projects: TemplateProjectLike[],
): React.ReactElement {
	const { LivePreview } = getPortfolioTemplate(template);
	return <LivePreview projects={projects} mode="preview" />;
}

export function renderPublicTemplateSection(
	template: PortfolioTemplate,
	projects: TemplateProjectLike[],
	onOpenProject: (projectId: string) => void,
	loadingProjectId: string | null,
): React.ReactElement {
	const { Section } = getPortfolioTemplate(template);
	return (
		<Section
			projects={projects}
			mode="public"
			onOpenProject={onOpenProject}
			loadingProjectId={loadingProjectId}
		/>
	);
}

export function renderPublicTemplateHeader(
	template: PortfolioTemplate,
	props: TemplateHeaderProps,
): React.ReactElement {
	const { Header } = getPortfolioTemplate(template);
	return <Header {...props} />;
}

export function SelectedTemplateBadge({
	template,
}: {
	template: PortfolioTemplate;
}): React.ReactElement {
	const definition = getPortfolioTemplate(template);

	return (
		<div className="inline-flex items-center gap-2 rounded-full border bg-muted/30 px-3 py-1 text-xs">
			<span className="font-semibold">Selected Template:</span>
			<span>{definition.name}</span>
		</div>
	);
}

export function TemplateDeltaNotice({
	selectedTemplate,
	savedTemplate,
	onApply,
	isSaving,
}: {
	selectedTemplate: PortfolioTemplate;
	savedTemplate: PortfolioTemplate;
	onApply?: () => void;
	isSaving?: boolean;
}): React.ReactElement | null {
	if (selectedTemplate === savedTemplate) return null;

	const definition = getPortfolioTemplate(selectedTemplate);

	return (
		<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-dashboard-warning-border bg-dashboard-warning-bg-soft p-3.5 text-xs text-dashboard-warning-strong">
			<div>
				<p className="font-semibold">Template change not saved yet</p>
				<p className="mt-0.5">
					You selected {definition.name}. Save to apply this template to your
					public page.
				</p>
			</div>
			{onApply && (
				<button
					type="button"
					onClick={onApply}
					disabled={isSaving}
					className="shrink-0 rounded-md bg-dashboard-accent px-3 py-1.5 text-xs font-semibold text-dashboard-on-fill shadow-xs hover:opacity-90 disabled:opacity-50 transition cursor-pointer"
				>
					{isSaving ? "Applying..." : "Apply to Public Portfolio"}
				</button>
			)}
		</div>
	);
}

export function ViewLayoutHint({
	template,
}: {
	template: PortfolioTemplate;
}): React.ReactElement {
	const definition = getPortfolioTemplate(template);
	return (
		<p className="text-xs text-muted-foreground">{definition.changesSummary}</p>
	);
}

export function ViewProjectButton({
	onClick,
}: {
	onClick: () => void;
}): React.ReactElement {
	return (
		<Button type="button" size="sm" variant="outline" onClick={onClick}>
			View Project
		</Button>
	);
}
